import { type BalanceDueReportRow } from '@utazzo/shared';
import { UzButton, UzCard, UzDatePicker, UzInput, UzSelect } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { formatDateDMY, formatInrPaise, isoToLocalDate, toDateInputIso } from '../invoices.helpers';
import { useBalanceDueReport, useInvoiceAgents } from '../use-invoices';

import { downloadExcel, downloadPdf } from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';

/**
 * Balance Due Report - report #13, a 1:1 port of the legacy
 * balanceduereport.aspx: deposit installments grouped by balance-due date
 * (newest first) with a per-date Sub Total and a Grand Total. Columns, colours
 * and grouping mirror the old screen; the row Edit icon becomes a link on the
 * proforma number.
 */

const PAYMENT_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'PAID', label: 'Paid' },
];

const rupees = (paise: string): number => Number(paise) / 100;

const EXPORT_COLUMNS = [
  {
    header: 'Due Date',
    value: (r: BalanceDueReportRow) => (r.dueDate ? formatDateDMY(r.dueDate) : '----'),
  },
  { header: 'Proforma No.', value: (r: BalanceDueReportRow) => r.proformaNumber ?? '' },
  { header: 'Name', value: (r: BalanceDueReportRow) => r.customerName ?? '' },
  { header: 'Agent', value: (r: BalanceDueReportRow) => r.agentName ?? '' },
  { header: 'Amount', value: (r: BalanceDueReportRow) => rupees(r.amountPaise) },
  { header: 'Status', value: (r: BalanceDueReportRow) => (r.paid ? 'Paid' : 'Unpaid') },
];

interface Group {
  label: string;
  rows: BalanceDueReportRow[];
}

export const BalanceDueReportPage = (): React.JSX.Element => {
  const [bookingId, setBookingId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState<'ALL' | 'PAID' | 'UNPAID'>('ALL');
  const [agent, setAgent] = useState('');

  const query = {
    ...(bookingId.trim() ? { bookingId: bookingId.trim() } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(amount.trim() ? { amount: Number(amount) } : {}),
    paymentStatus,
    ...(agent ? { agent } : {}),
  };
  const reportQuery = useBalanceDueReport(query);
  const rows = reportQuery.data?.rows ?? [];
  const totalPaise = reportQuery.data?.totalPaise ?? '0';

  const agentsQuery = useInvoiceAgents();
  const agentOptions = [
    { value: '', label: 'All agents' },
    ...(agentsQuery.data ?? []).map((a) => ({ value: a, label: a })),
  ];

  // Rows arrive ordered by due date DESC; group preserving that order.
  const groups = useMemo<Group[]>(() => {
    const out: Group[] = [];
    let current: Group | null = null;
    let currentKey = '\u0000';
    for (const row of rows) {
      const key = row.dueDate ? row.dueDate.slice(0, 10) : 'none';
      if (!current || key !== currentKey) {
        current = { label: row.dueDate ? formatDateDMY(row.dueDate) : '----', rows: [] };
        out.push(current);
        currentKey = key;
      }
      current.rows.push(row);
    }
    return out;
  }, [rows]);

  const groupTotal = (g: Group): string =>
    formatInrPaise(g.rows.reduce((acc, r) => acc + BigInt(r.amountPaise), 0n).toString());

  const handleExport = (fmt: 'xlsx' | 'pdf'): void => {
    try {
      if (fmt === 'pdf')
        downloadPdf(rows, EXPORT_COLUMNS, 'balance-due-report', 'Balance Due Report');
      else downloadExcel(rows, EXPORT_COLUMNS, 'balance-due-report');
    } catch {
      toast.error('Failed to export');
    }
  };

  return (
    <PageShell
      title="Balance Due Report"
      description="Deposit installments grouped by due date, with per-date sub-totals and a grand total."
      actions={
        <div className="flex items-center gap-2">
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={rows.length === 0}
            onClick={() => handleExport('pdf')}
          >
            PDF
          </UzButton>
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={rows.length === 0}
            onClick={() => handleExport('xlsx')}
          >
            Excel
          </UzButton>
        </div>
      }
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <UzInput
          label="Proforma No."
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
          containerClassName="w-44"
        />
        <UzDatePicker
          label="From"
          value={isoToLocalDate(from)}
          onValueChange={(d) => setFrom(d ? toDateInputIso(d) : '')}
          containerClassName="w-44"
        />
        <UzDatePicker
          label="To"
          value={isoToLocalDate(to)}
          onValueChange={(d) => setTo(d ? toDateInputIso(d) : '')}
          containerClassName="w-44"
        />
        <UzInput
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          containerClassName="w-36"
        />
        <UzSelect
          label="Payment status"
          value={paymentStatus}
          onValueChange={(v) => setPaymentStatus(v as 'ALL' | 'PAID' | 'UNPAID')}
          options={PAYMENT_OPTIONS}
          containerClassName="w-40"
        />
        <UzSelect
          label="Agent"
          value={agent}
          onValueChange={setAgent}
          options={agentOptions}
          containerClassName="w-48"
        />
      </div>

      <UzCard padding="none">
        {reportQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading report...</p>
        ) : reportQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the report.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">
            No balance-due installments match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-zinc-200 bg-[#e2f4ff] text-left text-xs font-semibold text-zinc-700">
                  <th className="px-3 py-2">S.No</th>
                  <th className="px-3 py-2">Proforma No.</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Agent</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.label}>
                    <tr>
                      <td
                        colSpan={6}
                        className="bg-[#3494c7] px-3 py-2 text-center text-sm font-bold text-white"
                      >
                        {g.label}
                      </td>
                    </tr>
                    {g.rows.map((row, i) => (
                      <tr
                        key={`${row.invoiceId}-${row.slot}`}
                        className="border-b border-zinc-100 last:border-0"
                      >
                        <td className="px-3 py-2 text-zinc-600 tabular-nums">{i + 1}</td>
                        <td className="px-3 py-2 tabular-nums">
                          <Link
                            to={`/invoices/${row.invoiceId}`}
                            className="text-primary hover:underline"
                          >
                            {row.proformaNumber ?? '-'}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-zinc-700">{row.customerName ?? '-'}</td>
                        <td className="px-3 py-2 text-zinc-700">{row.agentName ?? '-'}</td>
                        <td className="px-3 py-2 text-right font-medium text-red-600 tabular-nums">
                          {formatInrPaise(row.amountPaise)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <span className={row.paid ? 'text-green-700' : 'text-red-600'}>
                            {row.paid ? 'Paid' : 'Unpaid'}
                          </span>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-[#e2f4ff] text-sm font-semibold">
                      <td colSpan={4} className="px-3 py-2 text-right text-red-600">
                        Sub Total
                      </td>
                      <td className="px-3 py-2 text-right text-red-600 tabular-nums">
                        {groupTotal(g)}
                      </td>
                      <td className="px-3 py-2" />
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#ccffff] text-base font-bold">
                  <td colSpan={4} className="px-3 py-3 text-right text-red-600">
                    Grand Total
                  </td>
                  <td className="px-3 py-3 text-right text-red-600 tabular-nums">
                    {formatInrPaise(totalPaise)}
                  </td>
                  <td className="px-3 py-3" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </UzCard>
    </PageShell>
  );
};
